import { ACHIEVEMENTS } from '@am/protocol';
import { useState } from 'react';
import { achievementIconUrl, rankEmblemUrl } from '../art';
import { Icon, type IconName } from './icons';

/*
 * Achievement badges and rank emblems, painted (art/achievements, art/ranks). Until a picture is
 * in, or if it does not load, a medal in the achievement's tier colour with an icon stands in.
 */

/** The badge's metal, by the achievement's points: the harder, the finer. */
export type Tier = 'bronze' | 'silver' | 'gold' | 'platinum';

export function tierOf(points: number): Tier {
  return points >= 100 ? 'platinum' : points >= 50 ? 'gold' : points >= 25 ? 'silver' : 'bronze';
}

export function achievementTier(id: string): Tier {
  return tierOf(ACHIEVEMENTS.find((a) => a.id === id)?.points ?? 0);
}

/** What the stand-in medal shows for each achievement. */
const STAND_IN: Record<string, IconName> = {
  firstFlight: 'plane',
  flights100: 'plane',
  flights1000: 'plane',
  flights5000: 'plane',
  widebody: 'plane',
  privateJet: 'plane',
  helicopter: 'plane',
  military: 'hangar',
  threeRunways: 'runway',
  value1m: 'money',
  value10m: 'money',
  value50m: 'money',
  value200m: 'money',
  cash10m: 'money',
  debtFree: 'check',
  grant: 'money',
  airlines10: 'globe',
  rating4: 'star',
  rating5: 'star',
  fullService: 'bed',
  comfort: 'crown',
  freight: 'box',
  hub: 'link',
  international: 'globe',
  charter: 'dice',
  allMilestones: 'trophy',
  yearOne: 'clock',
  veteran: 'medal',
  towerTop: 'tower',
  terminal5: 'terminal',
  jetBridges: 'stand',
  metro: 'train',
  deicePad: 'snow',
  spotters: 'camera',
  redCarpet: 'crown',
  airshow: 'plane',
  collector: 'star',
  landlord: 'land',
  coop: 'users',
};

/** Pictures that did not load this session: asked for once only. */
const missing = new Set<string>();

/** An achievement's badge, `size` pixels square; greyed with a lock while it is still to earn. */
export function AchievementBadge({
  id,
  size,
  locked = false,
}: {
  id: string;
  size: number;
  locked?: boolean;
}) {
  const url = achievementIconUrl(id);
  const [broken, setBroken] = useState(() => missing.has(url));
  return (
    <span
      className={`ach-badge tier-${achievementTier(id)}${locked ? ' locked' : ''}`}
      style={{ width: size, height: size }}
    >
      {broken ? (
        <span className="ach-badge-medal">
          <Icon name={STAND_IN[id] ?? 'trophy'} size={Math.round(size * 0.46)} />
        </span>
      ) : (
        <img
          src={url}
          alt=""
          width={size}
          height={size}
          draggable={false}
          onError={() => {
            missing.add(url);
            setBroken(true);
          }}
        />
      )}
      {locked && (
        <span className="ach-badge-lock">
          <Icon name="lock" size={Math.max(10, Math.round(size * 0.26))} />
        </span>
      )}
    </span>
  );
}

/**
 * A rank's emblem, a pair of wings `size` pixels high and twice as wide (a crown until the
 * picture is in).
 */
export function RankEmblem({ rank, size }: { rank: string; size: number }) {
  const url = rankEmblemUrl(rank);
  const [broken, setBroken] = useState(() => missing.has(url));
  if (broken) return <Icon name="crown" size={Math.round(size * 0.52)} />;
  return (
    <img
      className="rank-emblem"
      src={url}
      alt=""
      width={size * 2}
      height={size}
      draggable={false}
      onError={() => {
        missing.add(url);
        setBroken(true);
      }}
    />
  );
}
